/**
 * Guscio delle viste operative: menu, selettore della squadra-stagione e,
 * per Mister e Dirigente su mobile, barra inferiore.
 *
 * Su desktop il menu e una colonna fissa per tutti; su mobile e un cassetto
 * che si apre dalla testata. Il Live Timer ne resta fuori (§1.3): la pagina
 * della partita usa il guscio solo prima della formazione e a partita finita.
 */

import { type ReactNode, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { MenuIcon, MessagesSquareIcon, SparklesIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { assistenteDisponibile, useAssistente } from '../assistente/contesto.js';
import { type SquadraStagione, useContesto } from './contesto.js';
import { MenuUtente } from './MenuUtente.js';
import { NonTrovata } from './NonTrovata.js';
import { SelettoreTema } from './SelettoreTema.js';
import { ricordaSquadra } from './ultimaSquadra.js';
import { type Sezione, barraInferiore, cambiaSquadra, menu } from './voci.js';

export function Guscio({
  children,
  squadra,
}: {
  children?: ReactNode;
  /**
   * Fuori dal prefisso di squadra: la squadra-stagione di cui disegnare menu e
   * barra, quando il percorso non la dice (vedi `GuscioSocieta`).
   */
  squadra?: string;
}) {
  const { ss: dalPercorso } = useParams<{ ss: string }>();
  const ss = squadra ?? dalPercorso;
  const { utente, squadre, erroreSquadre } = useContesto();
  const { pathname } = useLocation();
  const { stato, apri } = useAssistente();
  const [menuAperto, setMenuAperto] = useState(false);

  // Ogni cambio di pagina, dal menu come dal selettore, chiude il cassetto.
  useEffect(() => setMenuAperto(false), [pathname]);
  useEffect(() => {
    if (dalPercorso) ricordaSquadra(dalPercorso);
  }, [dalPercorso]);

  if (!squadre) return <p className="attesa">{erroreSquadre ?? 'Carico…'}</p>;

  // Una squadra-stagione inesistente e una di un'altra societa danno la
  // stessa risposta: il guscio non rivela quali esistono.
  const corrente = squadre.find((s) => s.id === ss);
  if (!ss || !corrente) return <NonTrovata />;

  const sezioni = menu(utente, ss);
  const barra = barraInferiore(utente, ss);
  // L'assistente e la quinta posizione della barra, ma non e una voce: apre
  // il pannello sopra la vista (§1.5).
  const conAssistente = barra.length > 0 && assistenteDisponibile(stato);

  return (
    <div
      className={cn(
        'min-h-dvh md:grid md:grid-cols-[260px_1fr]',
        // Le testate fisse delle viste si fermano sotto questa, che su
        // telefono resta in cima anche lei.
        '[--altezza-testata:calc(56px+env(safe-area-inset-top))] md:[--altezza-testata:0px]',
        // Le viste dentro il guscio leggono questa altezza per non finire
        // sotto la barra inferiore: vale anche per i comandi fissi, come il
        // «+» delle schede.
        barra.length > 0 &&
          '[--altezza-barra:calc(56px+env(safe-area-inset-bottom))] md:[--altezza-barra:0px]',
      )}
    >
      <header className="sticky top-0 z-30 flex h-(--altezza-testata) items-center gap-1 border-b-2 border-foreground bg-background px-2 pt-[env(safe-area-inset-top)] md:hidden">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMenuAperto(true)}
          aria-label="Apri il menu"
        >
          <MenuIcon className="size-6" />
        </Button>
        <span className="truncate text-[17px] font-semibold">
          {corrente.squadra} · {corrente.stagione}
        </span>
      </header>

      <aside className="sticky top-0 hidden h-dvh border-r-2 border-foreground md:block">
        <Menu
          sezioni={sezioni}
          ss={ss}
          testa={<Selettore squadre={squadre} corrente={corrente} />}
        />
      </aside>

      <Sheet open={menuAperto} onOpenChange={setMenuAperto}>
        <SheetContent
          side="left"
          showCloseButton={false}
          aria-describedby={undefined}
          className="w-[min(300px,85vw)] gap-0 border-r-2 border-foreground p-0 md:hidden"
        >
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <Menu
            sezioni={sezioni}
            ss={ss}
            testa={<Selettore squadre={squadre} corrente={corrente} />}
            onChiudi={() => setMenuAperto(false)}
          />
        </SheetContent>
      </Sheet>

      {/* Isolato perché nulla delle viste, come il tooltip dei grafici, salga
          sopra testata e barra inferiore scorrendo. */}
      <main className="@container isolate min-w-0 pb-(--altezza-barra)">
        {children ?? <Outlet />}
      </main>

      {barra.length > 0 && (
        <nav
          aria-label="Navigazione rapida"
          className="fixed inset-x-0 bottom-0 z-30 flex border-t-2 border-foreground bg-background pb-[env(safe-area-inset-bottom)] md:hidden"
        >
          {barra.map((v) => (
            <div key={v.chiave} className="flex min-h-14 flex-1 items-center justify-center">
              <Button asChild variant="ghost" className={classeBarra}>
                <NavLink
                  to={v.percorso(ss)}
                  end={v.chiave === 'cruscotto'}
                  aria-label={v.etichetta}
                  title={v.etichetta}
                >
                  <v.icona aria-hidden />
                </NavLink>
              </Button>
            </div>
          ))}
          {conAssistente && (
            <div className="flex min-h-14 flex-1 items-center justify-center">
              <Button
                variant="ghost"
                onClick={apri}
                aria-label="Assistente"
                title="Assistente"
                className={classeBarra}
              >
                <SparklesIcon aria-hidden />
              </Button>
            </div>
          )}
        </nav>
      )}
    </div>
  );
}

/**
 * Il comando della barra inferiore: solo l'icona della voce, la stessa del
 * menu, con il nome per i lettori di schermo. La voce attiva ha lo sfondo
 * della voce attiva del menu.
 */
const classeBarra =
  'h-10 w-16 rounded-full text-muted-foreground [&_svg]:size-6 aria-[current=page]:bg-accent aria-[current=page]:text-foreground';

/** La riga di una voce: le sezioni del catalogo e quelle del piede la condividono. */
const classeRiga = (attiva = false) =>
  cn(
    'flex min-h-12 w-full items-center gap-3 px-4 text-left text-base',
    // La voce attiva resta com'e anche sotto il puntatore: l'hover e delle altre.
    attiva
      ? 'bg-accent/70 font-bold shadow-[inset_4px_0_0_var(--foreground)]'
      : 'hover:bg-accent/40',
  );

function TitoloSezione({ children }: { children: ReactNode }) {
  return (
    <h2 className="px-4 pt-2.5 pb-1 text-[13px] font-semibold text-muted-foreground">{children}</h2>
  );
}

/**
 * Apre il pannello da qualunque guscio, quando l'assistente c'e, e porta allo
 * storico (V59). Hanno una sezione loro, dopo quelle del catalogo, perche
 * l'assistente e un pannello e non una vista (§1.5).
 */
function SezioneAssistente({ onApri }: { onApri?: () => void }) {
  const { stato, apri } = useAssistente();
  if (!assistenteDisponibile(stato)) return null;
  return (
    <section className="border-b border-border py-2">
      <TitoloSezione>Assistente IA</TitoloSezione>
      <button
        type="button"
        className={classeRiga()}
        onClick={() => {
          onApri?.();
          apri();
        }}
      >
        <SparklesIcon aria-hidden className="size-5 shrink-0" />
        Chiedi
      </button>
      <NavLink to="/assistente" onClick={onApri} className={({ isActive }) => classeRiga(isActive)}>
        <MessagesSquareIcon aria-hidden className="size-5 shrink-0" />
        Conversazioni
      </NavLink>
    </section>
  );
}

/**
 * Lo stesso menu nella colonna del desktop e nel cassetto del mobile, e nei
 * due gusci: cambia solo la testa, selettore di squadra o ritorno alle squadre.
 * Le sezioni stanno una sotto l'altra; il menu utente, con l'uscita, resta in
 * fondo.
 */
export function Menu({
  sezioni,
  ss = '',
  testa,
  onChiudi,
}: {
  sezioni: Sezione[];
  /** Fuori da una squadra le voci di squadra non ci sono e il percorso lo ignora. */
  ss?: string;
  testa: ReactNode;
  /** Nel cassetto del mobile: aprire l'assistente lo chiude, sta sopra la vista. */
  onChiudi?: () => void;
}) {
  return (
    <nav
      aria-label="Menu principale"
      className="flex h-full flex-col overflow-y-auto pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
    >
      {testa}

      {sezioni.map((sezione) => (
        // La sezione senza titolo, la Home, continua nella prossima senza separatore.
        <section
          key={sezione.titolo ?? 'testa'}
          className={cn('py-2', sezione.titolo && 'border-b border-border')}
        >
          {sezione.titolo && <TitoloSezione>{sezione.titolo}</TitoloSezione>}
          {sezione.voci.map((v, i) => (
            <div key={v.chiave}>
              {v.gruppo && v.gruppo !== sezione.voci[i - 1]?.gruppo && (
                <h3 className="px-4 pt-2.5 pb-1 text-[13px] font-semibold text-muted-foreground">
                  {v.gruppo}
                </h3>
              )}
              <NavLink
                to={v.percorso(ss)}
                end={v.chiave === 'cruscotto'}
                className={({ isActive }) => classeRiga(isActive)}
              >
                <v.icona aria-hidden className="size-5 shrink-0" />
                {v.etichetta}
              </NavLink>
            </div>
          ))}
        </section>
      ))}

      <SezioneAssistente onApri={onChiudi} />

      <section className="py-2">
        <TitoloSezione>Aspetto</TitoloSezione>
        <div className="px-4 pt-1 pb-2">
          <SelettoreTema />
        </div>
      </section>

      <footer className="sticky bottom-0 mt-auto border-t-2 border-foreground bg-background">
        <MenuUtente />
      </footer>
    </nav>
  );
}

/**
 * Sostituisce il prefisso del percorso e nient'altro: il contesto vive
 * nell'URL (§1.1), quindi cambiare squadra e cambiare pagina.
 */
function Selettore({
  squadre,
  corrente,
}: {
  squadre: SquadraStagione[];
  corrente: SquadraStagione;
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  if (squadre.length === 1) {
    return (
      <div className="flex flex-col gap-1 px-4 pt-4 pb-2">
        <span className="text-[13px] text-muted-foreground">{corrente.stagione}</span>
        <strong className="text-lg">{corrente.squadra}</strong>
      </div>
    );
  }

  // L'elenco arriva gia ordinato: stagione corrente in testa, poi le passate.
  const perStagione = new Map<string, SquadraStagione[]>();
  for (const s of squadre) perStagione.set(s.stagione, [...(perStagione.get(s.stagione) ?? []), s]);

  return (
    <div className="px-4 pt-4 pb-2">
      <Select
        value={corrente.id}
        onValueChange={(nuova) => navigate(cambiaSquadra(pathname, corrente.id, nuova))}
      >
        <SelectTrigger aria-label="Squadra" className="w-full font-semibold">
          {/* Stesso nome in piu stagioni: la stagione va detta anche qui. */}
          <SelectValue>
            {corrente.squadra} · {corrente.stagione}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {[...perStagione].map(([stagione, elenco]) => (
            <SelectGroup key={stagione}>
              <SelectLabel>{stagione}</SelectLabel>
              {elenco.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.squadra}
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
